import { StandardModal } from "@classdojo/web/components/modals";
import { BodyText, Button, HeadlineText, TextField } from "@classdojo/web/nessie";
import { ThemeUIStyleObject } from "@classdojo/web/nessie/stylingLib";
import { AT, autoTranslate } from "@web-monorepo/vite-auto-translate-plugin/runtime";
import keyBy from "lodash/keyBy";
import map from "lodash/map";
import React from "react";
import type { AnyAction } from "redux";
import { appMetadataGroups, Group } from "src/pods/groups";
import createGroups, { GroupRestriction } from "src/pods/groups/createGroups";
import { Student } from "src/pods/students";
import AppModal from "src/shared/components/AppModal";
import AppPage from "src/shared/components/AppPage";
import computeOptimalBaseAvatarURL from "src/utils/computeOptimalBaseAvatarURL";
import logEvent from "src/utils/logEvent";
import GroupRestrictions from "./GroupRestrictions";
import GroupsDisplay from "./GroupsDisplay";

const GROUP_OPTIONS = [{ size: 2 }, { size: 3 }, { size: 4 }, { size: 5 }, { size: 6 }];

type GroupsRemoteProps = {
  classId: string;
  mountPoint: string;
  displayOpen: (url: string, action?: AnyAction) => void;
  displayClose: VoidFunction;
  remoteClose: VoidFunction;
  students: Student[];
  editGroupRestrictions: ({
    classId,
    groupOfStudentIdGroups,
  }: {
    classId: string;
    groupOfStudentIdGroups: GroupRestriction[];
  }) => void;
  groupRestrictions: GroupRestriction[];
};

type GroupsRemoteState = {
  selectedGroupOption: { size: number } | null;
  selectedGroups: Group[] | null;
  askForGroupSize: boolean;
  sizeInput?: number;
  askForGroupRestrictions: boolean;
  baseAvatarURL?: string;
  errored?: boolean;
};

const initialState: GroupsRemoteState = {
  selectedGroupOption: null,
  selectedGroups: null,
  askForGroupSize: false,
  sizeInput: undefined,
  askForGroupRestrictions: false,
};

// eslint-disable-next-line react-prefer-function-component/react-prefer-function-component
export default class GroupsRemote extends React.Component<GroupsRemoteProps, GroupsRemoteState> {
  constructor(props: GroupsRemoteProps) {
    super(props);
    this.state = initialState;
  }

  componentDidMount() {
    logEvent({
      eventName: "groupcreator.setup.open",
    });
  }

  componentDidUpdate(prevProps: GroupsRemoteProps) {
    if (this.props.students && !prevProps.students && this.state.selectedGroupOption && !this.state.selectedGroups) {
      this.openDisplay(this.state.selectedGroupOption);
    }
  }

  componentWillUnmount() {
    logEvent({
      eventName: "groupcreator.setup.close",
    });
  }

  render() {
    const { students, groupRestrictions, classId, mountPoint } = this.props;
    const { sizeInput, askForGroupSize, selectedGroups, askForGroupRestrictions, baseAvatarURL, errored } = this.state;

    if (!students || !groupRestrictions) {
      return (
        <div sx={loadingContainerStyle}>
          <AT>Please wait...</AT>
        </div>
      );
    }

    if (Array.isArray(students) && students.length === 0) {
      return (
        <AppPage {...appMetadataGroups()} classId={classId} mountPoint={mountPoint}>
          <BodyText sx={errorContainerStyle}>Your class doesn't have any students :(</BodyText>
        </AppPage>
      );
    }

    function renderGroupOptions(this: GroupsRemote, groupOption: { size: number }) {
      return (
        <li key={groupOption.size} sx={groupMakerRemoteListItemStyle}>
          <button
            data-name="groups_remot:render_group_options:students_per_group"
            aria-label={autoTranslate("__size__ students per group", {
              size: groupOption.size.toFixed(),
            })}
            sx={groupMakerRemoteButtonStyle}
            onClick={() => this.openDisplay(groupOption)}
          >
            <span>{groupOption.size}</span>
          </button>
        </li>
      );
    }

    return (
      <AppPage
        {...appMetadataGroups()}
        classId={classId}
        mountPoint={mountPoint}
        disableFocusTrap={this.state.askForGroupSize || this.state.askForGroupRestrictions}
      >
        <article sx={appStyle}>
          <header>
            <HeadlineText as="h1" level={2} sx={titleStyle}>
              How many students per group?
            </HeadlineText>
          </header>
          <section sx={groupsStyle}>
            <ul sx={groupMakerRemoteListsStyle}>
              {GROUP_OPTIONS.map(renderGroupOptions, this)}
              <li sx={groupMakerRemoteListItemStyle}>
                <button
                  data-name="groups_remot:add_custom_group_number"
                  sx={groupMakerRemoteButtonStyle}
                  onClick={this.openGroupSizeInput}
                  aria-label={autoTranslate("Add custom group number")}
                >
                  <span>+</span>
                </button>
              </li>
            </ul>
            <Button
              size="s"
              kind="tertiary"
              data-name="groups_remote:render:group_maker_restrictions_open_modal_label"
              onClick={this.openGroupRestrictions}
              sx={{ marginX: "auto", marginTop: "dt_l" }}
            >
              Don't group together &gt;
            </Button>
          </section>
          {askForGroupSize && (
            <StandardModal
              data-name="groups_remote:render:standard_modal"
              headerStyle={{
                display: "none",
              }}
              primaryButton={{
                text: autoTranslate("Done"),
                props: {
                  "data-name": "groups_remote:done",
                  disabled: !!sizeInput && sizeInput < 1,
                  onClick: () => {
                    this.closeGroupSizeInput();
                    this.openDisplay({ size: sizeInput! });
                  },
                },
              }}
              secondaryButton={{
                text: autoTranslate("Cancel"),
                props: {
                  "data-name": "groups_remote:cancel",
                  onClick: this.closeGroupSizeInput,
                },
              }}
            >
              <TextField
                label={autoTranslate("Add custom group number")}
                type="number"
                pattern="\d*"
                placeholder="7"
                min={1}
                value={this.state.sizeInput}
                onChange={this.onSizeChange}
                autoFocus
              />
            </StandardModal>
          )}
          {selectedGroups && mountPoint === "mobile" && (
            <AppModal
              {...appMetadataGroups()}
              onRequestHide={() => {
                this.props.displayClose();
                this.props.remoteClose();
              }}
              onRequestStart={() => this.props.displayOpen("/apps/display/groups")}
              onRequestStop={() => this.props.displayClose()}
            >
              <GroupsDisplay baseAvatarURL={baseAvatarURL} groups={selectedGroups} />
            </AppModal>
          )}
          {askForGroupRestrictions && (
            <GroupRestrictions
              classId={this.props.classId}
              editGroupRestrictions={this.props.editGroupRestrictions}
              onRequestHide={this.closeGroupRestrictions}
              students={students}
              groupRestrictions={groupRestrictions}
            />
          )}
          {errored && (
            <StandardModal
              data-name="groups_remote:render:group_maker_could_not_create_groups"
              title={autoTranslate("Could not create groups")}
              primaryButton={{
                text: autoTranslate("OK"),
                props: {
                  "data-name": "groups_remote:ack_error",
                  onClick: () => this.setState({ errored: false }),
                },
              }}
            >
              <AT>Please try a different group size or remove students from 'Don't group together'.</AT>
            </StandardModal>
          )}
        </article>
      </AppPage>
    );
  }

  openGroupSizeInput = () => {
    this.setState({ askForGroupSize: true, sizeInput: undefined });
  };

  closeGroupSizeInput = () => {
    this.setState({ askForGroupSize: false });
  };

  onSizeChange = (value: string) => {
    const sizeInput = parseInt(value, 10);

    if (sizeInput <= 0) {
      this.setState({
        sizeInput: 1,
      });

      return;
    }

    this.setState({ sizeInput });
  };

  openDisplay = (groupOption: { size: number }) => {
    const { students, groupRestrictions } = this.props;

    const baseAvatarURL = computeOptimalBaseAvatarURL(map(students, "avatar"));

    const simplifiedStudents = keyBy(
      students.map((student) => ({
        _id: student._id,
        firstName: student.firstName,
        lastName: student.lastName,
        avatar: student.avatar.replace(baseAvatarURL!, ""),
      })),
      "_id",
    );

    const studentIds = students.map((s) => s._id);

    let groupsOfIds;
    try {
      groupsOfIds = createGroups(studentIds, groupOption.size, groupRestrictions);
      // eslint-disable-next-line no-catch-all/no-catch-all
    } catch {
      this.setState({ errored: true });
      return;
    }

    const groups = groupsOfIds.map((group) => ({ students: group.map((id) => simplifiedStudents[id]) }));

    this.setState({
      errored: false,
      selectedGroupOption: groupOption,
      selectedGroups: groups,
      baseAvatarURL: baseAvatarURL!,
    });

    this.props.displayOpen("/apps/display/groups", {
      type: "RECEIVE_GROUPS",
      payload: { groups, baseAvatarURL },
    });

    logEvent({
      eventName: GROUP_OPTIONS.includes(groupOption) ? "groupcreator.start" : "groupcreator.start.custom",
      eventValue: String(groupOption.size),
    });
  };

  closeDisplay = () => {
    this.setState({ selectedGroupOption: null, selectedGroups: null });
    this.props.displayClose();
  };

  openGroupRestrictions = () => {
    this.setState({ askForGroupRestrictions: true });
  };

  closeGroupRestrictions = () => {
    this.setState({ askForGroupRestrictions: false });
  };
}

const errorContainerStyle: ThemeUIStyleObject = {
  padding: "dt_l",
  textAlign: "center",
};

const loadingContainerStyle: ThemeUIStyleObject = {
  padding: "dt_l",
  textAlign: "center",
  fontSize: 16,
};

const appStyle: ThemeUIStyleObject = {
  textAlign: "center",
  padding: "dt_l",
};

const titleStyle: ThemeUIStyleObject = {
  marginBottom: "dt_l",
  paddingX: "dt_l",
};

const groupsStyle: ThemeUIStyleObject = {
  margin: "auto",
};

const groupMakerRemoteListsStyle: ThemeUIStyleObject = {
  display: "flex",
  flexWrap: "wrap",
  justifyContent: "center",
  margin: "0",
  padding: "0",
  listStyleType: "none",
};

const groupMakerRemoteListItemStyle: ThemeUIStyleObject = {
  flex: "0 90px",
  margin: "dt_s",
  "-webkit-touch-callout": "none",
  "-webkit-tap-highlight-color": "rgba(0, 0, 0, 0)",
  userSelect: "none",
};

const groupMakerRemoteButtonStyle: ThemeUIStyleObject = {
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  height: "90px",
  width: "90px",
  margin: "0",
  border: "dt_card",
  borderRadius: "dt_radius_s",
  backgroundColor: "dt_background_primary",
  fontSize: "48px",
  fontWeight: "800",
  textAlign: "center",
  verticalAlign: "middle",
  boxShadow: "dt_shadow_shadezies_small",
  cursor: "pointer",
  color: "dt_content_primary",
  "-webkit-touch-callout": "none",
  "-webkit-tap-highlight-color": "rgba(0, 0, 0, 0)",
  userSelect: "none",
};
